/**
 * @file cabinet-header-slots.ts
 * @description Места в верхней панели кабинета под заголовок страницы и её
 * главное действие (phase19.29, второй проход; phase19.31 шаг О4 — порог
 * сжатия и заголовок вкладки).
 *
 * Каркас кабинета (`PlatformLayout`, `ShopLayout`) кладёт в `AppHeader` два
 * пустых контейнера (название — слева, действие — справа), а в конец
 * содержимого — третий (закреплённая нижняя полоса: в DOM она идёт после
 * содержимого, поэтому Tab и программы чтения с экрана проходят «название →
 * содержимое → главная кнопка»). DOM-узлы раздаются через контекст; страница
 * рисует туда своё название, пояснение и кнопку порталом (`CabinetPageHeader`).
 * Портал, а не «данные в контексте»: кнопка остаётся в дереве страницы со
 * своими обработчиками и состоянием (загрузка, блокировка).
 *
 * phase19.31, О4:
 *  - `compactBelow` — до какой ширины заголовок «сжатый» (в панели только
 *    название и «⋯», главное действие — в нижней полосе): платформа — до 768
 *    (как было), кабинет магазина — до 1024 (вместе со шторкой меню);
 *  - заголовок вкладки браузера: страница объявляет своё название
 *    (`claimTitle`), каркас ставит его в `document.title`; свой заголовок
 *    каркаса (`fallbackTitle`) — только запасной, для страниц без
 *    `CabinetPageHeader`. Раньше эффект каркаса срабатывал ПОСЛЕ эффекта
 *    страницы (родитель после детей) и перебивал её название.
 */

import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";

/** Порог «сжатого» заголовка: телефон (< 768) или телефон и планшет (< 1024). */
export type HeaderCompactBelow = "md" | "lg";

export interface CabinetHeaderSlots {
  /** Контейнер слева в панели: название страницы и пояснение. */
  startEl: HTMLElement | null;
  /** Контейнер справа в панели: действия страницы (широкий экран) и «⋯». */
  endEl: HTMLElement | null;
  /** Контейнер в конце содержимого: закреплённая нижняя полоса. */
  bottomEl: HTMLElement | null;
  /** Окончание заголовка вкладки: «Обзор — Commora». */
  titleSuffix: string;
  /** До какой ширины заголовок сжатый. */
  compactBelow: HeaderCompactBelow;
  /** Страница объявляет название для вкладки; возвращает «снять». */
  claimTitle: (title: string) => () => void;
}

/** Контекст мест; каркас оборачивает дерево в `CabinetHeaderSlotsContext.Provider`. */
export const CabinetHeaderSlotsContext = createContext<CabinetHeaderSlots | null>(null);

/** Места в панели; null — каркас их не предоставляет. */
export function useCabinetHeaderSlots(): CabinetHeaderSlots | null {
  return useContext(CabinetHeaderSlotsContext);
}

interface SlotStateOptions {
  /** Порог сжатого заголовка; по умолчанию — телефон (как в кабинете платформы). */
  compactBelow?: HeaderCompactBelow;
  /**
   * Заголовок вкладки, когда страница своего не объявила (непереведённые
   * страницы). Не задан — каркас заголовок вкладки не трогает.
   */
  fallbackTitle?: string | null;
}

/**
 * Состояние мест для каркаса: ref-колбэки контейнеров, значение контекста и
 * заголовок вкладки.
 *
 * ```tsx
 * const slots = useCabinetHeaderSlotState("Мёд Алтая", { compactBelow: "lg", fallbackTitle: "Заказы — Мёд Алтая" });
 * <CabinetHeaderSlotsContext.Provider value={slots.value}>
 *   <AppHeader start={<div ref={slots.setStartEl} />} end={<div ref={slots.setEndEl} />} />
 * ```
 */
export function useCabinetHeaderSlotState(titleSuffix: string, options: SlotStateOptions = {}) {
  const { compactBelow = "md", fallbackTitle } = options;
  const [startEl, setStartEl] = useState<HTMLElement | null>(null);
  const [endEl, setEndEl] = useState<HTMLElement | null>(null);
  const [bottomEl, setBottomEl] = useState<HTMLElement | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);

  // Снятие сравнивает с текущим: при переходе новая страница успевает
  // объявить себя раньше, чем старая снимет своё название.
  const claimTitle = useCallback((title: string) => {
    setPageTitle(title);
    return () => setPageTitle((current) => (current === title ? null : current));
  }, []);

  // Один эффект на заголовок вкладки: название страницы важнее запасного.
  useLayoutEffect(() => {
    if (pageTitle) document.title = `${pageTitle} — ${titleSuffix}`;
    else if (fallbackTitle) document.title = fallbackTitle;
  }, [pageTitle, titleSuffix, fallbackTitle]);

  const value = useMemo<CabinetHeaderSlots>(
    () => ({ startEl, endEl, bottomEl, titleSuffix, compactBelow, claimTitle }),
    [startEl, endEl, bottomEl, titleSuffix, compactBelow, claimTitle],
  );
  return { value, setStartEl, setEndEl, setBottomEl };
}
